/**
 * ShopifyConnectModal — collects the .myshopify.com domain and kicks off
 * OAuth by redirecting the browser to /v1/shopify/install. Used by the
 * Sources page "+ Connect store" and the trigger drawer's
 * "+ Connect a new Shopify store" action.
 */

import { type FormEvent, useState } from "react";
import { ExternalLink, X } from "lucide-react";
import { getApiClient } from "../../lib/apiClient";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Where to land after OAuth completes. Defaults to /sources?shopify_connected={shop}. */
  returnTo?: string;
}

const SHOP_PATTERN = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i;

export function ShopifyConnectModal({ open, onClose, returnTo }: Props) {
  const [shop, setShop] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    let normalized = shop.trim().toLowerCase();
    // Allow merchants to paste either "mystore" or "mystore.myshopify.com".
    if (normalized && !normalized.includes(".")) {
      normalized = `${normalized}.myshopify.com`;
    }
    if (!SHOP_PATTERN.test(normalized)) {
      setError("That doesn't look like a valid .myshopify.com domain.");
      return;
    }
    const url = getApiClient().shopify.installURL(normalized, returnTo);
    if (!url || url.startsWith("#")) {
      setError("Connect Shopify isn't available in offline Studio mode.");
      return;
    }
    // Full-page redirect — Shopify needs the merchant in the loop for the
    // consent screen, then bounces back to our /v1/shopify/callback.
    window.location.assign(url);
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white dark:bg-neutral-950 shadow-xl border border-neutral-200 dark:border-neutral-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">Connect a Shopify store</h3>
            <p className="text-[11px] text-neutral-500 mt-0.5">
              We'll redirect you to Shopify to approve the install. You'll come
              back here when it's done.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-4 space-y-3">
          <label className="block">
            <span className="text-xs font-medium">Shop domain</span>
            <span className="block text-[11px] text-neutral-500 mt-0.5">
              The .myshopify.com domain (not your custom domain).
            </span>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="text"
                value={shop}
                onChange={(e) => setShop(e.target.value)}
                placeholder="mystore.myshopify.com"
                autoFocus
                className="flex-1 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-sm font-mono"
              />
            </div>
          </label>
          {error ? (
            <p className="text-xs text-category-error border border-category-error/30 bg-category-error/5 rounded-md px-2.5 py-2">
              {error}
            </p>
          ) : null}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-neutral-200 dark:border-neutral-700 px-3 py-1.5 text-xs font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center gap-1.5 rounded-md bg-category-trigger/10 border border-category-trigger/30 text-category-trigger px-3 py-1.5 text-xs font-medium hover:bg-category-trigger/20"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Continue to Shopify
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
